import { useGetChannelsApiV1Suspense } from "@/api/__generated__/api.schema";
import { Profile } from "@/components/profile";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetHeader,
  BottomSheetTitle,
  BottomSheetTrigger,
  Button,
  Checkbox,
  Collection,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  Icon,
  Typography,
} from "@backpac/monica";
import { forwardRef, useMemo, useState } from "react";

interface ChannelSelectProps {
  defaultValues?: string[];
  onChange: (values: string[]) => void;
}

interface Channel {
  id: string;
  name: string;
  customUrl: string;
  profileImageUrl: string;
}

export function WorkspaceManagedChannelSelect({ defaultValues = [], onChange }: ChannelSelectProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <WorkspaceManagedChannelSelect.BottomSheet defaultValues={defaultValues} onChange={onChange} />;
  }
  return <WorkspaceManagedChannelSelect.Dropdown defaultValues={defaultValues} onChange={onChange} />;
}

WorkspaceManagedChannelSelect.Dropdown = function WorkspaceManagedChannelSelectDropdown({
  defaultValues = [],
  onChange,
}: ChannelSelectProps) {
  const workspaceId = useWorkspaceId();
  const { data: channelsData } = useGetChannelsApiV1Suspense(workspaceId);
  const channels = channelsData?.data.items ?? [];

  const [open, setOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(defaultValues);

  const handleSelect = (channelId: string) => {
    const newSelectedValues = selectedValues.includes(channelId)
      ? selectedValues.filter((value) => value !== channelId)
      : [...selectedValues, channelId];

    setSelectedValues(newSelectedValues);
    onChange(newSelectedValues);
  };

  const displayText = useMemo(() => {
    if (selectedValues.length === 0) return "Select channels";
    if (selectedValues.length === channels.length) return "All";

    const selectedChannel = channels.find((channel) => channel.id === selectedValues[0]);
    if (!selectedChannel) return "Select channels";

    if (selectedValues.length === 1) {
      return selectedChannel.name;
    }
    return `${selectedChannel.name} + ${selectedValues.length - 1} more`;
  }, [channels, selectedValues]);

  return (
    <Dropdown open={open} onOpenChange={setOpen}>
      <WorkspaceManagedChannelSelect.DropdownTrigger currentValues={selectedValues} displayText={displayText} />
      <DropdownContent className="w-full min-w-[396px] p-2" align="start" sideOffset={4}>
        <Collection<Channel>
          data={channels}
          filter={{
            keys: ["name", "customUrl"],
          }}
        >
          <div className="mb-2">
            <Collection.SearchInput placeholder="Search channel" />
          </div>
          <Collection.Result<Channel>>
            {({ collections }) => (
              <div className="max-h-[300px] overflow-y-auto">
                {collections.length === 0 ? (
                  <div className="p-1 w-full text-center text-xs text-textSubtle">
                    {channels.length === 0 ? "No channels connected yet" : "No channels match your search"}
                  </div>
                ) : (
                  collections.map((channel) => (
                    <DropdownItem
                      key={channel.id}
                      onSelect={(e) => {
                        e.preventDefault();
                        handleSelect(channel.id);
                      }}
                      className="flex items-center gap-2 px-2 py-2 w-full"
                    >
                      <Checkbox
                        checked={selectedValues.includes(channel.id)}
                        onCheckedChange={() => handleSelect(channel.id)}
                        className="mr-2 h-5 w-5"
                        id={`channel-${channel.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <Profile
                          name={channel.name}
                          description={channel.customUrl}
                          profileImageUrl={channel.profileImageUrl}
                        />
                      </div>
                    </DropdownItem>
                  ))
                )}
              </div>
            )}
          </Collection.Result>
        </Collection>
      </DropdownContent>
    </Dropdown>
  );
};

WorkspaceManagedChannelSelect.BottomSheet = function WorkspaceManagedChannelSelectBottomSheet({
  defaultValues = [],
  onChange,
}: ChannelSelectProps) {
  const workspaceId = useWorkspaceId();
  const { data: channelsData } = useGetChannelsApiV1Suspense(workspaceId);
  const channels = channelsData?.data.items ?? [];

  const [open, setOpen] = useState(false);
  const [selectedValues, setSelectedValues] = useState<string[]>(defaultValues);

  const handleSelect = (channelId: string) => {
    const newSelectedValues = selectedValues.includes(channelId)
      ? selectedValues.filter((value) => value !== channelId)
      : [...selectedValues, channelId];

    setSelectedValues(newSelectedValues);
    onChange(newSelectedValues);
  };

  const displayText = useMemo(() => {
    if (selectedValues.length === 0) return "Select channels";
    if (selectedValues.length === channels.length) return "All";

    const selectedChannel = channels.find((channel) => channel.id === selectedValues[0]);
    if (!selectedChannel) return "Select channels";

    if (selectedValues.length === 1) {
      return selectedChannel.name;
    }
    return `${selectedChannel.name} + ${selectedValues.length - 1} more`;
  }, [channels, selectedValues]);

  return (
    <BottomSheet open={open} onOpenChange={setOpen}>
      <WorkspaceManagedChannelSelect.BottomSheetTrigger currentValues={selectedValues} displayText={displayText} />
      <BottomSheetContent>
        <BottomSheetHeader>
          <BottomSheetTitle className="sr-only">Select Channels</BottomSheetTitle>
        </BottomSheetHeader>
        <Collection<Channel>
          data={channels}
          filter={{
            keys: ["name", "customUrl"],
          }}
        >
          <div className="mb-2 px-4">
            <Collection.SearchInput placeholder="Search channels..." />
          </div>
          <Collection.Result<Channel>>
            {({ collections }) => (
              <div className="flex flex-col max-h-[480px] overflow-y-auto">
                {collections.length === 0 ? (
                  <div className="p-4 w-full text-center text-sm text-textSubtle">
                    {channels.length === 0 ? "No channels connected yet" : "No channels match your search"}
                  </div>
                ) : (
                  collections.map((channel) => (
                    <div key={channel.id} className="flex items-center gap-4 py-2 px-4">
                      <Checkbox
                        className="w-5 h-5"
                        checked={selectedValues.includes(channel.id)}
                        onCheckedChange={() => handleSelect(channel.id)}
                        id={`channel-mobile-${channel.id}`}
                      />
                      <button
                        type="button"
                        className="flex-1 text-left min-w-0"
                        onClick={() => handleSelect(channel.id)}
                      >
                        <Profile
                          name={channel.name}
                          description={channel.customUrl}
                          profileImageUrl={channel.profileImageUrl}
                        />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </Collection.Result>
        </Collection>
      </BottomSheetContent>
    </BottomSheet>
  );
};

interface TriggerProps {
  currentValues: string[];
  displayText: string;
}

WorkspaceManagedChannelSelect.DropdownTrigger = forwardRef<HTMLButtonElement, TriggerProps>(
  function WorkspaceManagedChannelSelectDropdownTrigger({ displayText }, ref) {
    return (
      <DropdownTrigger asChild>
        <Button ref={ref} type="button" size="md" variant="outlined" className="py-3 w-full justify-between">
          <Typography size="xs" className="truncate">
            {displayText}
          </Typography>
          <Icon name="chevron-down" className="size-3 shrink-0" />
        </Button>
      </DropdownTrigger>
    );
  },
);

WorkspaceManagedChannelSelect.BottomSheetTrigger = forwardRef<HTMLButtonElement, TriggerProps>(
  function WorkspaceManagedChannelSelectBottomSheetTrigger({ displayText }, ref) {
    return (
      <BottomSheetTrigger asChild>
        <Button ref={ref} type="button" size="md" variant="outlined" className="py-3 w-full justify-between">
          <Typography size="xs" className="truncate">
            {displayText}
          </Typography>
          <Icon name="chevron-down" className="size-3 shrink-0" />
        </Button>
      </BottomSheetTrigger>
    );
  },
);
